import { useEffect, useRef, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { IndividualsDuplexerChannel } from '@wix/ai-scheduling-assistant-common-lib/commonTypes';
import { API_BASE } from './api-base';
import { apiFetch } from './api-client';
import { queryKeys } from './query-client';
import { useDuplexerSubscription } from './use-duplexer-subscription';
import { TURN_STALE_AFTER_MS, shouldApplyFrame, signalUpdatedAt, turnActive, type TurnActivitySignal } from './turn-activity';

/**
 * "The Maestro is working right now" — a transient indicator, and transient is the whole design.
 *
 * The Maestro working in the chat is not a task and not an outreach. Nothing is stored: if the turn
 * produces a task or an outreach, those show up through their own entities; if it produces neither,
 * the only record is the chat. So this exists so a user who stepped away from the chat can see that
 * something is still happening, and for nothing else.
 *
 * PUSH, NOT POLL. The harness republishes the conversation entity on the CONVERSATION duplexer channel
 * on every iteration, and publishes the agent's step narration on ASSISTANT_CHAT. Both are already how
 * the open chat knows it is busy (`useRemoteTurn`), so an interval here would be a second mechanism for
 * a signal that is already delivered — one that is both slower to light up and wasteful when nothing is
 * happening, which is nearly always. The single fetch below is a SEED, not a poll: it covers landing on
 * a page mid-turn, where there is no push to have heard.
 *
 * It is only live WHILE THE CHAT IS CLOSED. With the chat open the transcript is the indicator, and a
 * second one beside it is noise.
 */

export type MaestroWorking = {
  working: boolean;
  /** One line on what it is doing, when it has narrated one. */
  action?: string;
  /** The conversation to open. */
  conversationId?: string;
};

const IDLE: MaestroWorking = { working: false };

/** The conversation entity as the CONVERSATION channel pushes it. */
interface ConversationPush extends TurnActivitySignal {
  id?: string;
  /** Only the Maestro's own chat counts — a SCHEDULER push is an outreach delivery, a PULSE one a routine. */
  source?: string;
}

/** A chat row as ASSISTANT_CHAT pushes it; the step narration is what "on what" reads. */
interface ChatPush {
  conversationId?: string;
  role?: string;
  content?: string;
}

async function fetchSeed(): Promise<MaestroWorking> {
  const res = await apiFetch(`${API_BASE}/api/maestro-status`, { credentials: 'include' });
  // An indicator is not worth an error state. A failed read means "we do not know", and the honest
  // rendering of that is nothing — never a claim that it is working.
  if (!res.ok) return IDLE;
  return (await res.json()) as MaestroWorking;
}

/**
 * @param chatOpen whether the chat is currently on screen. When it is, this neither seeds nor
 * subscribes, and reports idle — the transcript is already the indicator.
 */
export function useMaestroWorking(chatOpen: boolean): MaestroWorking {
  const seed = useQuery({ queryKey: queryKeys.maestroStatus, queryFn: fetchSeed, enabled: !chatOpen, staleTime: Infinity });

  const [live, setLive] = useState<MaestroWorking | null>(null);
  const [signal, setSignal] = useState<TurnActivitySignal | null>(null);
  // The newest frame applied, so an out-of-order older push cannot clear a live turn — the same guard
  // `useRemoteTurn` applies to the open chat's own busy state.
  const lastAppliedAtRef = useRef<number | null>(null);

  // Adopt the seed only if no push has spoken yet — a push is always newer than the load-time read, so
  // it must not be overwritten by a seed that resolves after it.
  const seedData = seed.data;
  useEffect(() => {
    if (!seedData) return;
    setLive((current) => current ?? (seedData.working ? seedData : IDLE));
  }, [seedData]);

  useDuplexerSubscription<ConversationPush>(IndividualsDuplexerChannel.CONVERSATION, (payload) => {
    if (chatOpen) return;
    const entity = payload.entity;
    const id = entity?.id ?? payload.entityId;
    if (!entity || !id) return;
    // A SCHEDULER conversation is an outreach delivery and a PULSE one is a routine's background run —
    // neither is "the Maestro working in my chat". An absent source is trusted: older pushes omit it,
    // and the alternative is an indicator that never lights.
    if (entity.source && entity.source !== 'SYMPHONY') return;
    const frameAt = signalUpdatedAt(entity);
    if (!shouldApplyFrame(frameAt, lastAppliedAtRef.current)) return;
    lastAppliedAtRef.current = frameAt;
    setSignal(entity);
    setLive((current) =>
      turnActive(entity) ? { working: true, conversationId: id, ...(current?.conversationId === id && current.action ? { action: current.action } : {}) } : IDLE,
    );
  });

  // "On what" — the agent's own step narration, pushed on the chat channel. No fetch: the same rows the
  // open chat renders are already arriving here.
  useDuplexerSubscription<ChatPush>(IndividualsDuplexerChannel.ASSISTANT_CHAT, (payload) => {
    if (chatOpen) return;
    const entity = payload.entity;
    if (entity?.role !== 'ASSISTANT_STEP' || !entity.content?.trim()) return;
    setLive((current) =>
      current?.working && (!entity.conversationId || entity.conversationId === current.conversationId)
        ? { ...current, action: entity.content!.trim() }
        : current,
    );
  });

  // The stamp is cleared by the turn that set it, so a turn that dies uncleanly publishes nothing
  // further. Without this the indicator, switched on by a real event, would never switch off — and an
  // indicator with no end is worse than none: it promises something that is not coming.
  useEffect(() => {
    if (!live?.working) return undefined;
    const timer = setTimeout(() => setLive((current) => (turnActive(signal) ? current : IDLE)), TURN_STALE_AFTER_MS);
    return () => clearTimeout(timer);
  }, [live?.working, signal]);

  if (chatOpen) return IDLE;
  return live ?? IDLE;
}
